import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Dropdown, Empty, Spin, Button, Tooltip } from 'antd';
import {
  BellOutlined, CheckOutlined, MailOutlined, ClockCircleOutlined, RightOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { notificationService } from '../services/affectationService';
import discussionService from '../services/discussionService';
import MentionCloche from './discussion/MentionCloche';
import axiosInstance from '../variables/api/axiosInstance';
import { can, HAB, CHARTE_COLORS as C } from '../variables/constants';
import { libelleDelai } from '../i18n/donnees';

/**
 * Cloche in-app - centre de notifications unique (bas de la sidebar).
 *
 * Agrège quatre flux dans un même panneau, chacun gardant sa source de vérité :
 *  - MENTIONS des discussions internes - /v1/discussions/mes-mentions ;
 *  - notifications in-app persistées (affectations de dossiers…) - /v1/notifications ;
 *  - mails non lus de la messagerie IMAP - /v1/messagerie/inbox/non-lus ;
 *  - relances du jour (alertes de délais CEDEAO/CIMA) - /v1/delais/mes-alertes.
 *
 * LES MENTIONS SONT EN TÊTE, ET DÉPLIÉES. C'est le seul flux auquel on RÉPOND : un
 * collègue attend. Les trois autres informent. Une mention qui n'afficherait qu'un
 * titre obligerait à ouvrir le dossier pour savoir si c'est urgent - exactement le
 * trajet que la cloche existe pour éviter.
 *
 * Le flux de mentions a sa propre route et n'est PAS fondu dans /v1/notifications :
 * répondre en ligne demande le corps du message, que le module de notification ne
 * transporte pas et n'a pas à transporter.
 *
 * Rafraîchi par polling toutes les 60 s - le projet n'a ni WebSocket ni SSE.
 */

const POLL_MS = 60000;

// Où mène le clic sur une notification in-app, selon l'entité visée.
const ROUTES = {
  SINISTRE: (id) => `/sinistres/${id}`,
  CONVERSATION: (id) => (id ? `/conversations/${id}` : '/conversations'),
};

const NIVEAU_COLORS = {
  CRITIQUE: '#dc2626',
  URGENT: '#ea580c',
  ATTENTION: '#d97706',
};

const tempsRelatif = (iso, t) => {
  if (!iso) return '';
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return t('notificationBell.alInstant');
  if (min < 60) return t('notificationBell.ilYAMinutes', { n: min });
  const h = Math.floor(min / 60);
  if (h < 24) return t('notificationBell.ilYAHeures', { n: h });
  const j = Math.floor(h / 24);
  return j === 1 ? t('notificationBell.hier') : t('notificationBell.ilYAJours', { n: j });
};

const joursRestantsLisible = (j, t) => {
  if (j == null) return '';
  if (j < 0) return t('notificationBell.echuDepuis', { jours: -j });
  if (j === 0) return t('notificationBell.echeanceAujourdhui');
  return t('notificationBell.joursRestants', { jours: j });
};

function SectionTitre({ children }) {
  return (
    <div style={{
      padding: '7px 12px 5px', fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
      textTransform: 'uppercase', color: C.faint, background: C.bg,
      borderBottom: `1px solid ${C.border}`,
    }}>
      {children}
    </div>
  );
}

function Ligne({ n, onClick }) {
  const { t } = useTranslation('commun');
  return (
    <div
      onClick={() => onClick(n)}
      style={{
        display: 'flex', gap: 10, padding: '10px 12px', cursor: 'pointer',
        borderBottom: `1px solid ${C.border}`,
        background: n.lu ? '#fff' : C.greenLight,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = C.bg; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = n.lu ? '#fff' : C.greenLight; }}
    >
      <span style={{
        width: 7, height: 7, borderRadius: '50%', marginTop: 6, flexShrink: 0,
        background: n.lu ? 'transparent' : C.greenMid,
      }} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{
          fontSize: 13, fontWeight: n.lu ? 500 : 600, color: C.text,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {n.titre}
        </div>
        {n.message && (
          <div style={{
            fontSize: 12, color: C.muted, marginTop: 2,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            overflow: 'hidden', whiteSpace: 'pre-line',
          }}>
            {n.message}
          </div>
        )}
        <div style={{ fontSize: 11, color: C.faint, marginTop: 3 }}>
          {tempsRelatif(n.createdAt, t)}
        </div>
      </div>
    </div>
  );
}

function LigneRelance({ a, onClick }) {
  const { t } = useTranslation('commun');
  const couleur = NIVEAU_COLORS[a.niveauAlerte] || C.muted;
  return (
    <div
      onClick={() => onClick(a)}
      style={{
        display: 'flex', gap: 10, padding: '9px 12px', cursor: 'pointer',
        borderBottom: `1px solid ${C.border}`, background: '#fff',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = C.bg; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
    >
      <span style={{
        width: 7, height: 7, borderRadius: '50%', marginTop: 6, flexShrink: 0,
        background: couleur,
      }} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{
          fontSize: 13, fontWeight: 600, color: C.text,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {libelleDelai(a) || a.codeDelai}
        </div>
        <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>
          {a.sinistreNumeroLocal && <span>{a.sinistreNumeroLocal} · </span>}
          <span style={{ color: couleur, fontWeight: 600 }}>{joursRestantsLisible(a.joursRestants, t)}</span>
        </div>
      </div>
    </div>
  );
}

export default function NotificationBell() {
  const { t } = useTranslation('commun');
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [nonLues, setNonLues] = useState(0);
  const [mailsNonLus, setMailsNonLus] = useState(0);
  const [alertes, setAlertes] = useState([]);
  const [items, setItems] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [mentionsNonLues, setMentionsNonLues] = useState(0);
  const [loading, setLoading] = useState(false);
  const monte = useRef(true);

  const peutDiscuter = can(HAB.DISCUSSION_READ);

  const rafraichirCompteurs = useCallback(async () => {
    // Chaque flux échoue en silence : un échec de poll ne doit pas alerter l'utilisateur.
    const [notifs, mails, delais, mentionsCount] = await Promise.allSettled([
      notificationService.compterNonLues(),
      axiosInstance.get('/v1/messagerie/inbox/non-lus', { skipErrorToast: true }),
      axiosInstance.get('/v1/delais/mes-alertes', { skipErrorToast: true }),
      peutDiscuter ? discussionService.compterMentionsNonLues() : Promise.resolve(0),
    ]);
    if (!monte.current) return;
    if (notifs.status === 'fulfilled') setNonLues(notifs.value.data?.data ?? 0);
    if (mails.status === 'fulfilled') setMailsNonLus(mails.value.data?.data ?? 0);
    if (delais.status === 'fulfilled') setAlertes(delais.value.data?.data ?? []);
    if (mentionsCount.status === 'fulfilled') setMentionsNonLues(mentionsCount.value ?? 0);
  }, [peutDiscuter]);

  useEffect(() => {
    monte.current = true;
    rafraichirCompteurs();
    const id = setInterval(rafraichirCompteurs, POLL_MS);
    return () => { monte.current = false; clearInterval(id); };
  }, [rafraichirCompteurs]);

  // On ne charge les listes qu'à l'ouverture : inutile de les tirer toutes les
  // minutes, seuls les compteurs ont besoin d'être frais.
  const ouvrir = async (estOuvert) => {
    setOpen(estOuvert);
    if (!estOuvert) return;
    setLoading(true);
    try {
      const [notifs, mentionsRecues] = await Promise.allSettled([
        notificationService.liste({ size: 15 }),
        peutDiscuter ? discussionService.mesMentions({ size: 8 }) : Promise.resolve({ items: [] }),
      ]);
      setItems(notifs.status === 'fulfilled' ? (notifs.value.data?.data ?? []) : []);
      setMentions(mentionsRecues.status === 'fulfilled' ? (mentionsRecues.value.items ?? []) : []);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Ouvrir le dossier sur le message exact.
   *
   * Le paramètre `discussion` déplie le panneau et surligne le message : sans lui,
   * l'agent atterrit sur un dossier de quinze onglets et doit retrouver seul ce dont
   * on lui parlait.
   */
  const allerVersMention = (mention) => {
    setOpen(false);
    if (mention.entiteType === 'SINISTRE') {
      navigate(`/sinistres/${mention.entiteTrackingId}?discussion=${mention.messageTrackingId}`);
    } else {
      navigate('/discussions');
    }
  };

  const allerVers = async (n) => {
    setOpen(false);
    if (!n.lu) {
      try {
        await notificationService.marquerLu(n.notificationTrackingId);
        setNonLues((c) => Math.max(0, c - 1));
      } catch { /* la navigation prime sur le marquage */ }
    }
    const route = ROUTES[n.lienType];
    if (route && n.lienTrackingId) navigate(route(n.lienTrackingId));
  };

  const allerVersRelance = (a) => {
    setOpen(false);
    if (a.sinistreTrackingId) navigate(`/sinistres/${a.sinistreTrackingId}?tab=delais`);
    else navigate('/relances');
  };

  const toutLire = async () => {
    try {
      await notificationService.marquerToutLu();
      setNonLues(0);
      setItems((prev) => prev.map((n) => ({ ...n, lu: true })));
    } catch { /* le compteur sera resynchronisé au prochain poll */ }
  };

  const total = nonLues + mailsNonLus + alertes.length + mentionsNonLues;
  const rienDuTout = items.length === 0 && mailsNonLus === 0 && alertes.length === 0
    && mentions.length === 0;

  const panneau = (
    <div style={{
      // Élargi depuis l'arrivée des mentions : une réponse en ligne dans 380 px de
      // large laisse trois mots visibles, ce qui décourage d'en écrire.
      width: 430, maxHeight: 560, display: 'flex', flexDirection: 'column',
      background: '#fff', borderRadius: 10, overflow: 'hidden',
      border: `1px solid ${C.border}`, boxShadow: '0 8px 28px rgba(0,0,0,0.14)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 12px', borderBottom: `1px solid ${C.border}`,
      }}>
        <span style={{ fontWeight: 600, fontSize: 13, color: C.text }}>
          {total > 0 ? t('notificationBell.titreAvecTotal', { total }) : t('notificationBell.titre')}
        </span>
        {nonLues > 0 && (
          <Button type="link" size="small" icon={<CheckOutlined />} onClick={toutLire}
            style={{ color: C.greenMid, fontSize: 12, padding: 0 }}>
            {t('notificationBell.toutMarquerLu')}
          </Button>
        )}
      </div>

      <div style={{ overflowY: 'auto', flex: 1 }}>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center' }}><Spin /></div>
        ) : rienDuTout ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<span style={{ color: C.muted, fontSize: 13 }}>{t('notificationBell.aucuneNotification')}</span>}
            style={{ padding: '28px 0' }}
          />
        ) : (
          <>
            {/* ── Mentions : le seul flux auquel on répond, donc le premier ── */}
            {mentions.length > 0 && (
              <>
                <SectionTitre>
                  <MessageOutlined style={{ marginRight: 6 }} />
                  {t('notificationBell.mentions', { nombre: mentions.length })}
                </SectionTitre>
                {mentions.map((m) => (
                  <MentionCloche
                    key={m.messageTrackingId}
                    mention={m}
                    onOuvrirDossier={allerVersMention}
                    onRepondu={rafraichirCompteurs}
                  />
                ))}
                <div
                  onClick={() => { setOpen(false); navigate('/discussions'); }}
                  style={{
                    padding: '7px 12px', fontSize: 12, color: C.greenMid, fontWeight: 600,
                    cursor: 'pointer', borderBottom: `1px solid ${C.border}`,
                  }}
                >
                  {t('notificationBell.toutesMesDiscussions')} <RightOutlined style={{ fontSize: 10 }} />
                </div>
              </>
            )}

            {/* ── Relances / alertes de délais du jour ── */}
            {alertes.length > 0 && (
              <>
                <SectionTitre>
                  <ClockCircleOutlined style={{ marginRight: 6 }} />
                  {t('notificationBell.relancesDuJour', { nombre: alertes.length })}
                </SectionTitre>
                {alertes.slice(0, 6).map((a) => (
                  <LigneRelance key={a.id} a={a} onClick={allerVersRelance} />
                ))}
                <div
                  onClick={() => { setOpen(false); navigate('/relances'); }}
                  style={{
                    padding: '7px 12px', fontSize: 12, color: C.greenMid, fontWeight: 600,
                    cursor: 'pointer', borderBottom: `1px solid ${C.border}`,
                  }}
                >
                  {t('notificationBell.toutesLesRelances')} <RightOutlined style={{ fontSize: 10 }} />
                </div>
              </>
            )}

            {/* ── Messagerie ── */}
            {mailsNonLus > 0 && (
              <>
                <SectionTitre>
                  <MailOutlined style={{ marginRight: 6 }} />
                  {t('notificationBell.messagerie')}
                </SectionTitre>
                <div
                  onClick={() => { setOpen(false); navigate('/messagerie'); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                    cursor: 'pointer', borderBottom: `1px solid ${C.border}`, background: C.greenLight,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = C.bg; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = C.greenLight; }}
                >
                  <MailOutlined style={{ color: C.greenMid, fontSize: 16 }} />
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text, flex: 1 }}>
                    {t('notificationBell.mailsNonLus', { count: mailsNonLus })}
                  </div>
                  <RightOutlined style={{ fontSize: 10, color: C.faint }} />
                </div>
              </>
            )}

            {/* ── Notifications in-app (affectations…) ── */}
            {items.length > 0 && (
              <>
                <SectionTitre>{t('notificationBell.dossiers')}</SectionTitre>
                {items.map((n) => <Ligne key={n.notificationTrackingId} n={n} onClick={allerVers} />)}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={ouvrir}
      trigger={['click']}
      placement="topRight"
      dropdownRender={() => panneau}
    >
      <Tooltip title={t('notificationBell.titre')} placement="right">
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 34, height: 34, borderRadius: 8, cursor: 'pointer', flexShrink: 0,
          background: 'rgba(255,255,255,0.10)',
        }}>
          <Badge count={total} size="small" offset={[2, -2]}>
            <BellOutlined style={{ fontSize: 17, color: '#fff' }} />
          </Badge>
        </div>
      </Tooltip>
    </Dropdown>
  );
}
